import React, { HTMLAttributes, useEffect, useRef } from "react";
import {
  useApplyVideoObjectFit,
  useAudioVideo,
  useRemoteVideoTileState,
} from "amazon-chime-sdk-component-library-react";
import { useVideoTileGridState } from "../../../video/providers/VideoTileGridProvider";
import { VideoUITile } from "./VideoUITile";
import { RoomMember } from "@platform/features/video/types/video.types";

interface Props extends Omit<HTMLAttributes<HTMLDivElement>, "css"> {
  /** The tile ID to bind the video element to */
  tileId: number;
  /** The attendee ID for this tile */
  attendeeId: string;
  /** The name to show on the video's nameplate */
  name?: string;
  isWithContentShare?: boolean;
  roomMemberData?: RoomMember | null;
}

export const VideoUITileRemoteVideoContainer: React.FC<
  React.PropsWithChildren<Props>
> = ({
  name,
  className,
  tileId,
  attendeeId,
  isWithContentShare,
  roomMemberData,
  ...rest
}) => {
  const audioVideo = useAudioVideo();
  const videoEl = useRef<HTMLVideoElement>(null);
  const { tileIdToAttendeeId } = useRemoteVideoTileState();
  const { attendeeStates } = useVideoTileGridState();
  useApplyVideoObjectFit(videoEl);

  // Check if video is enabled for this attendee
  const isVideoEnabled = attendeeStates[attendeeId]?.videoEnabled || false;

  // Only bind video element if we have a valid tile ID (not 0)
  const hasValidTileId = tileId > 0;

  useEffect(() => {
    if (!audioVideo || !videoEl.current || !hasValidTileId) {
      return;
    }

    audioVideo.bindVideoElement(tileId, videoEl.current);

    return () => {
      const tile = audioVideo.getVideoTile(tileId);
      if (tile) {
        audioVideo.unbindVideoElement(tileId);
      }
    };
  }, [audioVideo, tileId, hasValidTileId]);

  return (
    <VideoUITile
      {...rest}
      ref={videoEl}
      isWithContentShare={isWithContentShare}
      nameplate={name}
      className={className}
      isEnabled={isVideoEnabled && hasValidTileId}
      attendeeId={attendeeId}
      roomMemberData={roomMemberData}
    />
  );
};

export default VideoUITileRemoteVideoContainer;
